import {
  buildFieldNameForDisplayName,
  checkIsValidFieldName,
  type CustomFieldType,
} from "@repo/app-types/entities/customField";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { type FC, useState } from "react";
import { useCreateUserCustomFieldDefinitionMutation } from "../../../api/entities/userCustomField.js";
import {
  UserCustomFieldDefinitionForm,
  type UserCustomFieldSupportedResourceType,
} from "./userCustomFieldDefinitionForm.js";
import { useShouldShowCustomerInteractionResourceType } from "./useShouldShowCustomerInteractionResourceType.js";
import { useShouldShowInterviewResourceType } from "./useShouldShowInterviewResourceType.js";

export interface UserCustomFieldDefinitionCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The key is derived from the name until the user edits it, so an invalid key is
 * always something they typed — except for a name with no usable characters,
 * which derives an empty key. Stay quiet until they have typed either one.
 */
function getFieldNameError(
  fieldName: string,
  displayName: string,
): string | undefined {
  if (checkIsValidFieldName(fieldName)) {
    return undefined;
  }

  if (fieldName === "" && displayName.trim() === "") {
    return undefined;
  }

  return "Key must contain only lowercase letters, numbers, and underscores";
}

export const UserCustomFieldDefinitionCreateDialog: FC<
  UserCustomFieldDefinitionCreateDialogProps
> = ({ isOpen, onClose }) => {
  const [createUserCustomFieldDefinition, { isLoading }] =
    useCreateUserCustomFieldDefinitionMutation();

  const showInterviewResourceType = useShouldShowInterviewResourceType();
  const showCustomerInteractionResourceType =
    useShouldShowCustomerInteractionResourceType();

  const [resourceType, setResourceType] =
    useState<UserCustomFieldSupportedResourceType | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [fieldType, setFieldType] = useState<CustomFieldType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [customFieldName, setCustomFieldName] = useState("");
  const autoFieldName = buildFieldNameForDisplayName(displayName);
  const fieldName = customFieldName || autoFieldName;
  const fieldNameError = getFieldNameError(fieldName, displayName);

  // TODO: Figure out how to avoid having to do this.
  const resetForm = () => {
    setDisplayName("");
    setCustomFieldName("");
    setFieldType(null);
    setResourceType(null);
    setError(null);
  };

  const handleCancel = () => {
    onClose();
    resetForm();
  };

  const handleSubmit = async () => {
    // This check already occurs with `isFormValid` but adding for type safety
    if (
      !resourceType ||
      !displayName.trim() ||
      !checkIsValidFieldName(fieldName) ||
      !fieldType
    ) {
      console.error("Resource, name, key, and field type are required");
      setError("Resource, name, key, and type are all required.");
      return;
    }

    setError(null);

    try {
      await createUserCustomFieldDefinition({
        resource_type: resourceType,
        display_name: displayName,
        field_name: fieldName,
        field_type: fieldType,
      }).unwrap();

      onClose();
      resetForm();
    } catch (error: any) {
      console.error("Failed to create user custom field definition:", error);

      // Handle specific error for duplicate field names
      if (error.status === 409) {
        setError(
          error.data?.error ||
            "A field with a similar name already exists for this resource type",
        );
      } else {
        setError("Failed to create field. Please try again.");
      }
    }
  };

  const isFormValid =
    resourceType !== null &&
    displayName.trim() !== "" &&
    fieldType !== null &&
    fieldNameError === undefined;

  return (
    <>
      <Modal isOpen={isOpen} onCancel={handleCancel}>
        <ModalHeader onClose={handleCancel}>Create custom field</ModalHeader>

        <div className="my-4 space-y-4">
          <UserCustomFieldDefinitionForm
            resourceType={resourceType}
            displayName={displayName}
            fieldName={fieldName}
            fieldNameError={fieldNameError}
            fieldType={fieldType}
            showInterviewResourceType={showInterviewResourceType}
            showCustomerInteractionResourceType={
              showCustomerInteractionResourceType
            }
            onResourceTypeChange={setResourceType}
            onDisplayNameChange={setDisplayName}
            onFieldNameChange={setCustomFieldName}
            onFieldTypeChange={setFieldType}
          />

          {error && <div className="text-sm text-destructive">{error}</div>}
        </div>

        <div className="flex justify-end space-x-2">
          <Button variant={ButtonVariant.Secondary} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!isFormValid}
          >
            Create field
          </Button>
        </div>
      </Modal>
    </>
  );
};
